import { ref } from 'vue';
import { apiClient, API_PATHS } from '@/services/api';

export type AssignmentTarget =
  | { kind: 'incident'; incidentId: string; incidentName: string }
  | {
      kind: 'classification';
      classification: 'standard' | 'overshoot' | 'ev_reconcile' | 'drive_away';
    };

export interface AssignmentItem {
  orderUuid: string;
  result: 'processed' | 'conflict' | 'failed';
  reason?: string;
  orderState?: string | null;
  partnerName?: string | null;
}

export interface AssignmentSession {
  sessionId: string;
  target: AssignmentTarget;
  mode: 'single' | 'bulk';
  actor: { uid: string; displayName: string; email?: string };
  reason: string;
  status: 'completed' | 'partial' | 'failed';
  totals: { input: number; processed: number; conflict: number; failed: number };
  items: AssignmentItem[];
  createdAt: string;
  completedAt: string;
}

export function useAssignmentSession() {
  const submitting = ref(false);
  const error = ref<string | null>(null);
  const session = ref<AssignmentSession | null>(null);

  async function submit(target: AssignmentTarget, reason: string, orderUuids: string[]) {
    submitting.value = true;
    error.value = null;
    try {
      const response = await apiClient.rpIntegration.post(
        API_PATHS.rpIntegration.payOperations.orderAmendments.assignmentSessions,
        { target, reason, orderUuids },
      );
      session.value = response.data as AssignmentSession;
      return session.value;
    } catch (e: any) {
      error.value = e?.response?.data?.message ?? e?.message ?? 'Submission failed';
      throw e;
    } finally {
      submitting.value = false;
    }
  }

  async function load(sessionId: string) {
    const response = await apiClient.rpIntegration.get(
      API_PATHS.rpIntegration.payOperations.orderAmendments.assignmentSessionById(sessionId),
    );
    session.value = response.data as AssignmentSession;
    return session.value;
  }

  return { submitting, error, session, submit, load };
}
